import type {
  BusinessIncreaseRuleAirDensityPrice,
  BusinessIncreaseRuleAirPrice,
  BusinessIncreaseRuleAirWeightTier,
  BusinessIncreaseRulePriceComponent
} from '@/api/sass/business-increase-rules/types'

/** 空运列表字段与标准重量段的对应关系。 */
export const AIR_LIST_FIELD_TO_TIER: Record<string, BusinessIncreaseRuleAirWeightTier> = {
  minPrice: 'M',
  nPrice: 'N',
  p45: '45+',
  p100: '100+',
  p300: '300+',
  p500: '500+',
  p700: '700+',
  p1000: '1000+',
  p2000: '2000+'
}

/** 空运矩阵成本字段与标准重量段的对应关系。 */
export const AIR_MATRIX_FIELD_TO_TIER: Record<string, BusinessIncreaseRuleAirWeightTier> = {
  m: 'M',
  n: 'N',
  p45: '45+',
  p100: '100+',
  p300: '300+',
  p500: '500+',
  p700: '700+',
  p1000: '1000+',
  p2000: '2000+'
}

/** 空运标准重量段读取原始矩阵时支持的历史字段别名。 */
const AIR_TIER_ALIASES: Record<BusinessIncreaseRuleAirWeightTier, string[]> = {
  M: ['MIN', 'M', 'm'],
  N: ['N', 'n'],
  '45+': ['45+', 'P45', 'p45'],
  '100+': ['100+', 'P100', 'p100'],
  '300+': ['300+', 'P300', 'p300'],
  '500+': ['500+', 'P500', 'p500'],
  '700+': ['700+', 'P700', 'p700'],
  '1000+': ['1000+', 'P1000', 'p1000'],
  '2000+': ['2000+', 'P2000', 'p2000']
}

/**
 * 将原始矩阵字段编码转换为标准重量段编码。
 *
 * @param code 原始矩阵字段编码
 * @returns 标准重量段；未知字段返回 undefined
 */
export const resolveAirWeightTier = (code?: string): BusinessIncreaseRuleAirWeightTier | undefined => {
  if (!code) return undefined
  return Object.entries(AIR_TIER_ALIASES)
    .find(([, aliases]) => aliases.includes(code))?.[0] as BusinessIncreaseRuleAirWeightTier | undefined
}

/** 可用于定位商务价格密度行的原始矩阵信息。 */
export interface AirBusinessDensityLocator {
  /** 标准密度等级编码。 */
  densityLevelId?: string
  /** 密度等级显示名称。 */
  densityLabel?: string
  /** 体积重量比起始值。 */
  volRatio?: number
}

/** 判断两个可空密度起始值是否表示同一档。 */
const sameVolRatio = (left?: number, right?: number): boolean =>
  left !== undefined && right !== undefined && Math.abs(Number(left) - Number(right)) < 0.0001

/**
 * 在商务加价结果中查找指定密度行；未指定定位信息时返回基础密度行。
 *
 * @param businessPrice 空运商务加价结果
 * @param locator 原始矩阵密度信息
 * @returns 对应商务价格密度行
 */
export const findAirBusinessDensityRow = (
  businessPrice?: BusinessIncreaseRuleAirPrice | null,
  locator?: AirBusinessDensityLocator
): BusinessIncreaseRuleAirDensityPrice | undefined => {
  const rows = businessPrice?.rows ?? []
  if (!rows.length) return undefined
  if (locator) {
    const matched = rows.find(row =>
      (locator.densityLevelId && row.densityLevelId === locator.densityLevelId)
      || (locator.volRatio !== undefined && sameVolRatio(row.volRatio, locator.volRatio))
      || (locator.densityLabel && row.densityLabel === locator.densityLabel)
    )
    if (matched) return matched
  }
  return rows.find(row => sameVolRatio(row.volRatio, 0)) ?? rows[0]
}

/**
 * 读取指定密度和重量段的商务价格组成。
 *
 * @param record 含 businessPrice 的空运响应
 * @param tier 标准重量段
 * @param locator 原始矩阵密度信息
 * @returns 商务价格组成
 */
export const getAirBusinessPriceComponent = (
  record: { businessPrice?: BusinessIncreaseRuleAirPrice | null },
  tier: BusinessIncreaseRuleAirWeightTier,
  locator?: AirBusinessDensityLocator
): BusinessIncreaseRulePriceComponent | undefined =>
  findAirBusinessDensityRow(record.businessPrice, locator)?.tiers?.[tier]

/**
 * 按标准重量段及历史别名读取原始矩阵价格。
 *
 * @param prices 原始矩阵价格映射
 * @param tier 标准重量段
 * @returns 首个非空价格
 */
export const getAirMatrixTierPrice = (
  prices: Record<string, number | string | null | undefined> | undefined,
  tier: BusinessIncreaseRuleAirWeightTier
): number | string | null | undefined => {
  if (!prices) return undefined
  for (const key of AIR_TIER_ALIASES[tier]) {
    const value = prices[key]
    if (value !== null && value !== undefined && value !== '') return value
  }
  return undefined
}

/**
 * 将列表常用重量段字段规范为后端派生的商务销售价。
 *
 * @param record 空运列表或详情响应
 * @returns 不修改入参的商务销售价视图
 */
export const withAirBusinessListPrices = <T extends Record<string, any>>(record: T): T => {
  if (!record?.businessPrice) return record
  const normalized = { ...record }
  Object.entries(AIR_LIST_FIELD_TO_TIER).forEach(([field, tier]) => {
    const component = getAirBusinessPriceComponent(record, tier)
    if (component?.sellPrice !== null && component?.sellPrice !== undefined) {
      Object.assign(normalized, { [field]: component.sellPrice })
    }
  })
  return normalized
}

/**
 * 构造空运详情矩阵单行的成本价和商务销售价字段。
 *
 * @param record 含 businessPrice 的空运详情响应
 * @param matrixRow 原始费率矩阵行
 * @returns 可直接用于详情表格的价格字段
 */
export const buildAirBusinessMatrixFields = (
  record: { businessPrice?: BusinessIncreaseRuleAirPrice | null },
  matrixRow: Record<string, any>
): Record<string, number | string | boolean | null | undefined> => {
  const locator: AirBusinessDensityLocator = {
    densityLabel: matrixRow?.densityLabel,
    volRatio: matrixRow?.volRatio
  }
  const fields: Record<string, number | string | boolean | null | undefined> = {}
  Object.entries(AIR_MATRIX_FIELD_TO_TIER).forEach(([field, tier]) => {
    const costPrice = getAirMatrixTierPrice(matrixRow?.costPrices, tier)
    const component = getAirBusinessPriceComponent(record, tier, locator)
    fields[field] = costPrice === null || costPrice === undefined ? null : Number(costPrice)
    fields[`${field}Sell`] = component?.sellPrice === null || component?.sellPrice === undefined
      ? fields[field]
      : Number(component.sellPrice)
    fields[`${field}Applied`] = Boolean(component?.applied)
  })
  return fields
}

/**
 * 运价查询优先显示后端已计算的最终卖价，成本和商务管理视图沿用原逻辑。
 *
 * @param record 空运查询详情
 * @param matrixRow 包含最终 sellPrices 的矩阵行
 * @returns 查询详情可直接展示的价格字段
 */
export const buildAirQueryMatrixFields = (
  record: { businessPrice?: BusinessIncreaseRuleAirPrice | null },
  matrixRow: Record<string, any>
): Record<string, number | string | boolean | null | undefined> => {
  const fields = buildAirBusinessMatrixFields(record, matrixRow)
  Object.entries(AIR_MATRIX_FIELD_TO_TIER).forEach(([field, tier]) => {
    const finalPrice = getAirMatrixTierPrice(matrixRow?.sellPrices, tier)
    if (finalPrice !== null && finalPrice !== undefined) fields[`${field}Sell`] = Number(finalPrice)
  })
  return fields
}
